import React, { useState } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import PatientProfileV2 from '../../../../Components/PatientProfile/PatientProfileV2';
import { usePatientTreatmentContext } from './ContextProvider';
import SubTabNavigationStepper from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepper';
import apiClient from '../../../../Services/ApiClient';
import { useAppContext } from '../../../../Services/AppContext';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import { useEffectAsync } from '../../../../helpers';
import useCertificates from '../../../Certificates/Hooks/useCertificates';
import OverlayLoading from '../../../../Components/OverlayLoading';
import PatientConsentRequestButton from '../PatientConsentRequestButton/PatientConsentRequestButton';
import PatientDataVerificationVZPResultModal from '../DataVerification/VZP/PatientDataVerificationVZPResultModal';
import PatientDataVerificationCSSZResultModal from '../DataVerification/CSSZ/PatientDataVerificationCSSZResultModal';
import { PatientDataVerificationCSSZResultExtra } from '../DataVerification/CSSZ/PatientDataVerificationCSSZResult';
import { PatientDataVerificationVZPResultExtra } from '../DataVerification/VZP/PatientDataVerificationVZPResult';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import PatientVerifyInsuranceButton from '../../../Nczi/Components/PatientInsuranceSetup/PatientVerifyInsuranceButton';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import PatientVerifyInformationsButton from '../../../Nczi/Components/PatientInformationsVerify/PatientVerifyInformationsButton';

interface Props {
	workplaceId: string;
}

const PatientTreatmentProfile: React.FC<Props> = ({ workplaceId }) => {
	const { patientId } = usePatientTreatmentContext();
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const [processing, setProcessing] = useState(false);
	const [refresh, setRefresh] = useState(false);
	const {
		onOpen: onVZPResultOpen,
		isOpen: isVZPResultOpen,
		onClose: onVZPResultClose,
	} = useDisclose();
	const [vzpResultData, setVZPResultData] =
		useState<PatientDataVerificationVZPResultExtra | null>(null);
	const {
		onOpen: onCSSZResultOpen,
		isOpen: isCSSZResultOpen,
		onClose: onCSSZResultClose,
	} = useDisclose();
	const [csszResultData, setCSSZResultData] =
		useState<PatientDataVerificationCSSZResultExtra | null>(null);
	const [isPSZConfirmationOpen, setIsPSZConfirmationOpen] = useState(false);

	const {
		userData: { id: userId },
		globalTemplate,
	} = useAppContext();
	const { showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const { processResult } = useRestApiClient();
	const showCzechServices = globalTemplate === 'default';
	const showSlovakiaServices = globalTemplate === 'slovakia';
	const {
		checkWorkplaceCertificateAvailability,
		checkUserCertificateAvailability,
	} = useCertificates();

	const handleVerifyDoctors = async () => {
		setProcessing(true);

		const qualifiedCertificate = await checkUserCertificateAvailability(
			userId,
			'qualified',
		);

		if (qualifiedCertificate) {
			const result = await apiClient.put(
				`/patients/${patientId}/vzp/registrujici-lekari`,
				[],
			);
			console.log('result', result);
			processResult(result, () => {
				setIsPSZConfirmationOpen(true);
			});
			setRefresh(true);
			setRefresh(false);
		} else {
			showNotificationAlert({
				content: t('component.certificates.noCertificatesVerify'),
			});
		}

		setProcessing(false);
	};

	const handleVerifyInsurance = async () => {
		setProcessing(true);

		const qualifiedCertificate = await checkUserCertificateAvailability(
			userId,
			'qualified',
		);

		if (qualifiedCertificate) {
			const data: any = await apiClient.post(`/patients/${patientId}/vzp`, []);
			if (data?.data?.extra) {
				setVZPResultData(
					data.data.extra as PatientDataVerificationVZPResultExtra,
				);
				onVZPResultOpen();
			}
			setRefresh(true);
			setRefresh(false);
		} else {
			showNotificationAlert({
				content: t('component.certificates.noCertificatesVerify'),
			});
		}
		setProcessing(false);
	};

	const handleVerifyAddress = async () => {
		setProcessing(true);

		const commercialCertificate = await checkWorkplaceCertificateAvailability(
			workplaceId,
			'commercial',
		);

		if (commercialCertificate) {
			const data: any = await apiClient.put(
				`/patients/${patientId}/trvala-adresa`,
				[],
			);
			if (data?.data?.extra) {
				setCSSZResultData(
					data.data.extra as PatientDataVerificationCSSZResultExtra,
				);
				onCSSZResultOpen();
			}
			setRefresh(true);
			setRefresh(false);
		} else {
			showNotificationAlert({
				content: t('component.certificates.noCertificatesVerify'),
			});
		}
		setProcessing(false);
	};

	useEffectAsync(async () => {
		const tempSteps: NavigationStepperItemType[] = [
			{
				name: 'patientTreatment.profile.title',
				key: 'profile',
				component: (params: NavigationStepperComponentParams) => (
					<PatientProfileV2
						patientId={patientId}
						verifyInsurance={handleVerifyInsurance}
						verifyAddress={handleVerifyAddress}
						workplaceId={workplaceId}
					/>
				),
			},
		];

		if (showCzechServices) {
			const certificatePromises = [
				checkUserCertificateAvailability(userId, 'qualified'),
				checkWorkplaceCertificateAvailability(workplaceId, 'commercial'),
			];

			const [qualifiedCertificate, commercialCertificate] = await Promise.all(
				certificatePromises,
			);

			tempSteps.push({
				name: 'patientTreatment.profile.verifyInsurance.title',
				key: 'verifyInsurance',
				disabled: !qualifiedCertificate,
				tooltip: !qualifiedCertificate
					? 'component.certificates.noCertificatesVerify'
					: null,
				onClick: handleVerifyInsurance,
			});

			tempSteps.push({
				name: 'patientTreatment.profile.verifyAddress.title',
				key: 'verifyAddress',
				disabled: !commercialCertificate,
				tooltip: !commercialCertificate
					? 'component.certificates.noCertificatesVerify'
					: null,
				onClick: handleVerifyAddress,
			});

			tempSteps.push({
				name: 'patientTreatment.profile.verifyDoctors.title',
				key: 'verifyDoctors',
				disabled: !qualifiedCertificate,
				tooltip: !qualifiedCertificate
					? 'component.certificates.noCertificatesVerify'
					: null,
				onClick: handleVerifyDoctors,
			});
		}

		if (showSlovakiaServices) {
			tempSteps.push({
				name: 'patientTreatment.profile.verifyInsurance.title',
				key: 'verifyInsurance',
				disabled: false,
				tooltip: null,
				renderButton: () => (
					<PatientVerifyInsuranceButton patientId={patientId} />
				),
				onClick: () => {},
			});
			tempSteps.push({
				name: 'patientTreatment.profile.verifyInformations.title',
				key: 'verifyInformations',
				disabled: false,
				tooltip: null,
				renderButton: () => (
					<PatientVerifyInformationsButton patientId={patientId} />
				),
				onClick: () => {},
			});
		}

		tempSteps.push({
			name: 'patientTreatment.profile.askForPermission.title',
			key: 'askForPermission',
			renderButton: () => (
				<PatientConsentRequestButton
					patientId={patientId}
					setIsLoading={setProcessing}
				/>
			),
			onClick: () => {},
		});

		setSteps(tempSteps);
	}, []);

	if (refresh) {
		return <></>;
	}

	return (
		<>
			{processing && <OverlayLoading />}
			<SubTabNavigationStepper
				steps={steps}
				initialStepPath='profile'
				canSkip={true}
			/>
			{vzpResultData && (
				<PatientDataVerificationVZPResultModal
					isOpen={isVZPResultOpen}
					onClose={onVZPResultClose}
					extra={vzpResultData}
				/>
			)}
			{csszResultData && (
				<PatientDataVerificationCSSZResultModal
					isOpen={isCSSZResultOpen}
					onClose={onCSSZResultClose}
					extra={csszResultData}
				/>
			)}
			{isPSZConfirmationOpen && (
				<ConfirmationModal
					isOpen
					title='component.pszResultConfirmation.title'
					text={t('component.pszResultConfirmation.text')}
					onClose={() => setIsPSZConfirmationOpen(false)}
				/>
			)}
		</>
	);
};

export default PatientTreatmentProfile;
